/** UserWay corner inset on mobile (px from viewport right and bottom edges). */
export const USERWAY_MOBILE_INSET_PX = 6;

const MOBILE_MEDIA = '(max-width: 767.98px)';
const STYLE_ID = 'fmla-uw-mobile-inset';

let resizeListener: (() => void) | null = null;
let observer: MutationObserver | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Minimal NgZone shape so this util stays framework-light. */
type ZoneRunner = { runOutsideAngular: <T>(fn: () => T) => T };
// Runs work off the Angular zone so our DOM writes never schedule change detection.
let runOutsideAngular: <T>(fn: () => T) => T = fn => fn();

function isMobile(): boolean {
  return window.matchMedia(MOBILE_MEDIA).matches;
}

function findUserWayHosts(): HTMLElement[] {
  const hosts = new Set<HTMLElement>();

  for (const child of Array.from(document.body.children)) {
    if (!(child instanceof HTMLElement)) {
      continue;
    }
    const idClass = `${child.id} ${child.className}`;
    if (!/userway|uwy|a11y/i.test(idClass) && !child.shadowRoot) {
      continue;
    }
    if (child.shadowRoot && !/userway|uwy/i.test(idClass)) {
      const inner = child.shadowRoot.querySelector('[id*="userway"], [class*="uwy"], [class*="userway"]');
      if (!inner) {
        continue;
      }
    }
    let node: HTMLElement = child;
    while (node.parentElement && node.parentElement !== document.body) {
      if (window.getComputedStyle(node.parentElement).position === 'fixed') {
        node = node.parentElement;
      } else {
        break;
      }
    }
    hosts.add(node);
  }

  document.querySelectorAll<HTMLElement>('[id*="userway"], [class*="uwy"], .userway[class*="userway_p"]').forEach(el => {
    let node: HTMLElement | null = el;
    while (node && node !== document.body) {
      if (window.getComputedStyle(node).position === 'fixed') {
        hosts.add(node);
        return;
      }
      node = node.parentElement;
    }
  });

  return Array.from(hosts);
}

function applyInsetToHost(host: HTMLElement): void {
  const inset = `${USERWAY_MOBILE_INSET_PX}px`;
  // UserWay may set inset/margin/transform on the host; force symmetric corner placement.
  host.style.setProperty('inset', `auto ${inset} ${inset} auto`, 'important');
  host.style.setProperty('bottom', inset, 'important');
  host.style.setProperty('right', inset, 'important');
  host.style.setProperty('left', 'auto', 'important');
  host.style.setProperty('top', 'auto', 'important');
  host.style.setProperty('margin', '0', 'important');
  host.style.setProperty('margin-right', '0', 'important');
  host.style.setProperty('margin-bottom', '0', 'important');
  host.style.setProperty('padding', '0', 'important');
  host.style.setProperty('transform', 'none', 'important');
  host.style.setProperty('width', 'auto', 'important');

  const root = host.shadowRoot;
  if (!root) {
    return;
  }

  let tag = root.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_ID;
    root.appendChild(tag);
  }

  tag.textContent = `
    :host {
      inset: auto ${inset} ${inset} auto !important;
      bottom: ${inset} !important;
      right: ${inset} !important;
      left: auto !important;
      top: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      transform: none !important;
      width: auto !important;
    }

    /* Inner trigger often carries extra right margin from UserWay defaults. */
    :host button,
    :host [role="button"],
    :host #userwayAccessibilityIcon,
    :host [class*="uwy"],
    :host [class*="userway"] {
      margin: 0 !important;
      right: auto !important;
      left: auto !important;
      transform: none !important;
    }
  `;

  root.querySelectorAll<HTMLElement>('button, [role="button"], #userwayAccessibilityIcon, [class*="uwy"], [class*="userway"]').forEach(el => {
    el.style.setProperty('margin', '0', 'important');
    el.style.setProperty('transform', 'none', 'important');
  });

  fineTuneRightInset(host);
}

/** Match right-edge gap to bottom (UserWay inner trigger often adds extra right offset). */
function fineTuneRightInset(host: HTMLElement): void {
  const root = host.shadowRoot;
  const measureEl = (
    root?.querySelector('#userwayAccessibilityIcon, button, [role="button"]') as HTMLElement | null
  ) || host;
  const rect = measureEl.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }

  const gapRight = window.innerWidth - rect.right;
  const desired = USERWAY_MOBILE_INSET_PX;
  const delta = gapRight - desired;
  if (Math.abs(delta) < 0.5) {
    return;
  }

  const computed = window.getComputedStyle(host);
  const currentRight = parseFloat(computed.right);
  const currentBottom = parseFloat(computed.bottom);
  const baseRight = Number.isFinite(currentRight) ? currentRight : desired;
  const baseBottom = Number.isFinite(currentBottom) ? currentBottom : desired;
  const nextRight = Math.max(0, baseRight - delta);

  host.style.setProperty('right', `${nextRight}px`, 'important');
  host.style.setProperty('inset', `auto ${nextRight}px ${baseBottom}px auto`, 'important');
}

export function applyUserWayMobileCornerInset(): void {
  if (!isMobile()) {
    return;
  }

  findUserWayHosts().forEach(applyInsetToHost);

  // Legacy embed: position classes on body
  if (/userway_p\d/.test(document.body.className)) {
    const inset = `${USERWAY_MOBILE_INSET_PX}px`;
    document.body.style.setProperty('bottom', inset, 'important');
    document.body.style.setProperty('right', inset, 'important');
  }
}

function scheduleApply(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = runOutsideAngular(() => setTimeout(() => {
    debounceTimer = null;
    // Our own style writes mutate the DOM; pause the observer across the apply so it
    // can never re-trigger itself into an infinite mutation loop.
    if (observer) {
      observer.disconnect();
    }
    try {
      applyUserWayMobileCornerInset();
    } finally {
      if (observer) {
        observer.observe(document.body, { childList: true });
      }
    }
  }, 200));
}

/**
 * Keep UserWay tucked in the bottom-right corner on mobile after async widget load.
 *
 * Runs entirely outside the Angular zone and observes only direct childList changes on
 * <body> (where the widget is injected). Observing the full body subtree for style/class
 * changes — combined with this callback writing inline styles — created a self-sustaining
 * MutationObserver loop that pegged the main thread (see /events lockup).
 */
export function startUserWayMobilePositionWatcher(ngZone?: ZoneRunner): void {
  if (ngZone) {
    runOutsideAngular = fn => ngZone.runOutsideAngular(fn);
  }

  runOutsideAngular(() => {
    scheduleApply();

    if (!resizeListener) {
      resizeListener = () => scheduleApply();
      window.addEventListener('resize', resizeListener);
    }

    if (observer) {
      return;
    }

    observer = new MutationObserver(() => scheduleApply());
    observer.observe(document.body, { childList: true });

    // UserWay loads after cookie consent; retry briefly.
    for (let i = 1; i <= 12; i++) {
      window.setTimeout(scheduleApply, i * 500);
    }
  });
}
